import { ISubWindowFacade, ISubWindowParameters, ISubWindowsService } from '../sub-windows';
import { SubWindowsHostViewModel } from '../../components/global/sub-window/sub-windows-host.viewmodel';

/**
 * Implementation of ISubWindowsService which display sub-windows using the the SubWindowHost previously mounted into the react markup
 * and passed in the service through the `activateSubWindowsHost` function.
 *
 * There could be only single SubWindowHost active at a time.
 * Whenever new sub-window host becomes active -
 * all `outer` (e.g. previously active) hosts are deactivated - e.g. sub-windows hosted in them are being closed.
 */
class SubWindowsService extends ISubWindowsService {
    // holds the list of previously mounted sub-window hosts, not yet deactivated explicitly (e.g. still mounted), but not being active
    private mountedSubWindowHosts: SubWindowsHostViewModel[];
    // active sub window host - the one, used for actually showing sub windows.
    private activeSubWindowHost: SubWindowsHostViewModel;

    constructor() {
        super();

        this.mountedSubWindowHosts = [];
    }

    public showSubWindow<T>(parameters: ISubWindowParameters<T>): ISubWindowFacade {
        if (this.activeSubWindowHost) {
            return this.activeSubWindowHost.showSubWindow(parameters);
        } else {
            throw new Error('unable to show sub-window, there are no active sub-window host component');
        }
    }

    /**
     * must be called once SubWindowHost is mounted in order to activate it and deactivate all `outer` (e.g. previously mounted) ones.
     * @param hostViewModel
     */
    public activateSubWindowsHost(hostViewModel: SubWindowsHostViewModel) {
        if (this.activeSubWindowHost) {
            this.activeSubWindowHost.deactivate();
        }

        this.mountedSubWindowHosts.push(hostViewModel);
        this.activeSubWindowHost = hostViewModel;
    }

    /**
     * must be called once SubWindowHost is unmounted to deactivate it and release the reference.
     * `outer` (e.g. previously mounted) host becomes active then.
     * @param hostViewModel
     */
    public deactivateSubWindowsHost(hostViewModel: SubWindowsHostViewModel) {
        const deactivatedHostIndex = this.mountedSubWindowHosts.indexOf(hostViewModel);
        if (deactivatedHostIndex !== -1) {
            this.mountedSubWindowHosts.splice(deactivatedHostIndex, 1);
        }
        if (this.activeSubWindowHost === hostViewModel) {
            hostViewModel.deactivate();
        }

        const lastMountedItemIndex = this.mountedSubWindowHosts.length - 1;
        if (lastMountedItemIndex >= 0) {
            this.activeSubWindowHost = this.mountedSubWindowHosts[lastMountedItemIndex];
        } else {
            this.activeSubWindowHost = null;
        }
    }
}

export { SubWindowsService };
